/**
 * Billing — what this tenant's validations have given away.
 *
 * The History tab already lists per-redemption discounts ("N most recent"), so
 * a manager could see individual amounts and never a total, a period, or a
 * trend. This is where the totals belong.
 *
 * ─── ⚠️ IT REPORTS A VALUE, NOT AN AMOUNT DUE ────────────────────────────────
 * The single hardest thing about this screen is the label on the big number.
 * Validation funding exists at PROGRAM grain, so one tenant's period can mix
 * included, chargeback, and prepaid offers. This aggregate is the value those
 * programs gave away; it is not an amount due and cannot be converted into one
 * by looking at the tenant-wide total.
 *
 * Every money string here is therefore framed as the VALUE OF DISCOUNTS GIVEN,
 * the screen says out loud that it isn't a bill, and it points the manager at
 * the Validation tab for each offer's funding terms. Same discipline as #1736
 * and tenants/permit_billing.ts — say nothing about money you cannot attribute.
 *
 * ─── NOTHING HERE CHARGES ANYTHING ───────────────────────────────────────────
 * Read-only, deliberately and permanently on this surface. No pay button, no
 * card, no Stripe. An invoice is issued by a human in the ops console, and
 * collection (when it lands) is a hosted Stripe invoice emailed to the tenant —
 * never a card captured here.
 *
 * ─── THE VALIDATION TOTAL IS PARTIAL BY DESIGN, AND SAYS SO ──────────────────
 * Staff parking is absent from every validation total. The server sends
 * `omissions` precisely so this screen can state that rather than let a manager
 * infer their staff parking is free. A zero-valued "Staff parking" line would be
 * the fabrication we're avoiding, so the note carries no figure at all.
 *
 * ─── TWO SECTIONS, TWO MEANINGS, NEVER ONE TOTAL ─────────────────────────────
 * The staff-parking ESTIMATE below is a different kind of thing from the
 * discount accrual above it, and the screen keeps them visibly apart:
 *
 *   discounts given   a VALUE the tenant may owe none of — whoever funds the
 *                     validations, it is not established to be them.
 *   the estimate      current spaces × rates their OPERATOR typed into the
 *                     ops console. A report, not an amount due.
 *
 * Summing the two would produce a number describing nothing a person can act on,
 * so there is deliberately no grand total anywhere on this screen.
 *
 * ⚠️ AND THE ESTIMATE IS NOT A STATEMENT OR BILL FROM PARKIFY. Every rate on
 * it came from the operator; Parkify supplies only the multiplication. No due
 * date, no terms, no balance, no pay button — see api/src/tenants/permit_rates.ts.
 * The framing sits ABOVE the figure and explicitly says it is report-only.
 *
 * ⚠️ ABSENT IS ABSENT, NEVER $0.00. A tenant whose operator has entered no rates
 * sees NO estimate — not a zeroed one, which would be a false statement about
 * money. A failed read is a THIRD state and says so, because "your operator
 * removed your rates" and "we couldn't read them" must not look identical.
 */
import { useEffect, useRef, useState } from 'react'
import type { TenantSession } from '../lib/session'
import {
  getTenantBilling,
  listRegistrations,
  type PermitStatement,
  type PermitStatementLine,
  type TenantAccrual,
  type TenantInvoiceRecord,
  type TenantInvoiceStatus,
  type TenantPermitBilling,
} from '../lib/api'
import { statementStatusSummary } from '../lib/accountState'
import { labelStyle, purpleBtn } from '../ui'


const money = (cents: number) =>
  `$${(Math.max(0, cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const fmtDate = (iso: string) => {
  // Parsed as a LOCAL date, not UTC: `new Date('2026-08-31')` is midnight UTC,
  // which renders as Aug 30 anywhere west of Greenwich — an off-by-one on the
  // period boundary this whole feature exists to get right.
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * An INSTANT (not a calendar date) in the tenant's own clock — "Aug 18, 3:42 PM
 * EDT".
 *
 * Separate from `fmtDate` because the two are different kinds of value and
 * formatting one as the other is the bug this whole feature has to avoid:
 * `periodStart` is a calendar date parsed LOCALLY to dodge the UTC off-by-one,
 * while `countedAt` is a real instant that must be rendered in the LOT's zone so
 * "counted at" agrees with the clock the period was measured on.
 */
function fmtDateTime(iso: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

/**
 * The clock the period was counted on, as a human abbreviation ("EDT").
 *
 * Shown because the boundary genuinely matters: a validation redeemed at 11pm on
 * the 31st is in THIS period, and a manager reconciling against their own
 * records needs to know which midnight we mean. Resolved AT the period's start
 * instant so a summer period reads EDT and a winter one EST, rather than
 * whichever the browser happens to be in today.
 */
function tzLabel(timezone: string, atIso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      .formatToParts(new Date(atIso))
      .find(p => p.type === 'timeZoneName')?.value ?? timezone
  } catch {
    return timezone
  }
}

const periodLabel = (p: TenantAccrual) =>
  `${fmtDate(p.periodStart)} – ${fmtDate(p.periodEnd)}`

/**
 * Fixed column widths, so a longer label never drags a row sideways.
 *
 * Both measured against the widest string each column actually renders, at
 * 1512px. Widen them if a longer one is ever added — that is cheaper than
 * rediscovering the drift from a screenshot.
 *   STATUS_COL  widest pill is "ISSUED" at 68px
 *   RATE_COL    widest line is "2 × $180.00/mo" at ~104px
 */
const STATUS_COL = 76
const RATE_COL = 116

const STATUS_TONE: Record<TenantInvoiceStatus, { bg: string; fg: string; border: string; label: string }> = {
  // "Draft" is the tenant-facing word for "not sent to you" — an invoice their
  // operator is still preparing. It must not read as something owed.
  draft: { bg: 'var(--color-gray-100)', fg: 'var(--color-gray-600)', border: 'var(--color-gray-200)', label: 'Draft' },
  issued: { bg: 'rgba(245,158,11,0.12)', fg: '#92400e', border: 'rgba(245,158,11,0.35)', label: 'Issued' },
  paid: { bg: 'rgba(21,128,61,0.10)', fg: '#15803d', border: 'rgba(21,128,61,0.30)', label: 'Paid' },
  void: { bg: 'var(--color-gray-100)', fg: 'var(--color-gray-600)', border: 'var(--color-gray-200)', label: 'Void' },
}

function SummaryTile({ value, label, note }: { value: string; label: string; note?: string }) {
  return (
    <div style={{ flex: '1 1 180px', minWidth: 160, border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '14px 15px', background: '#fff' }}>
      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.4px' }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 800, marginTop: 3 }}>{label}</div>
      {note && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>{note}</div>}
    </div>
  )
}

function issuedCount(accrual: TenantAccrual | null): string {
  if (!accrual) return '—'
  const line = accrual.lines.find(l => l.kind === 'validation_discounts')
  return line ? String(line.quantity) : '0'
}

function AccountState({ invoicedBy, invoices }: {
  invoicedBy: 'parkify_bills' | 'operator_bills'
  invoices: TenantInvoiceRecord[]
}) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
      <div style={{ flex: '1 1 220px', minWidth: 0, border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '13px 14px', background: '#FCFBFD' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--color-gray-600)', marginBottom: 4 }}>Reporting cadence</div>
        <div style={{ fontSize: 15, fontWeight: 800 }}>Monthly</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>A new usage reporting period starts each calendar month.</div>
      </div>
      <div style={{ flex: '1 1 280px', minWidth: 0, border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '13px 14px', background: '#FCFBFD' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--color-gray-600)', marginBottom: 4 }}>Statement status</div>
        <div style={{ fontSize: 15, fontWeight: 800 }}>
          {invoicedBy === 'operator_bills' ? 'Managed by your operator' : statementStatusSummary(invoices)}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2, lineHeight: 1.45 }}>
          {invoicedBy === 'operator_bills'
            ? 'They set the invoice schedule and track payment outside Parkify.'
            : 'These are statement records only. No due date or payable balance is tracked in this portal.'}
        </div>
      </div>
    </div>
  )
}

function BillingSummary({ current, invoicedBy, permitBilling, permitCount }: {
  current: TenantAccrual
  invoicedBy: 'parkify_bills' | 'operator_bills'
  permitBilling: TenantPermitBilling | null
  permitCount: number | null
}) {
  const permits = permitCount == null ? '—' : String(permitCount)
  if (invoicedBy === 'operator_bills') {
    return (
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <SummaryTile value={permits} label="Permits in use" />
        <SummaryTile value={issuedCount(current)} label="Validations issued" />
        <SummaryTile value={money(current.totalCents)} label="Usage reported" note="discount value this period" />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
      <SummaryTile
        value={permits}
        label={permitBilling?.model === 'overage' ? 'Staff spaces in use' : 'Permits billed'}
        note={permitBilling?.model === 'overage' ? `${permitBilling.includedPermitCount} included` : undefined}
      />
      <SummaryTile value={issuedCount(current)} label="Validations issued" />
      <SummaryTile value={money(current.totalCents)} label="Discount value" note="this period · not an amount due" />
    </div>
  )
}

function OperatorBillingCard() {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ ...labelStyle, marginBottom: 8 }}>Who invoices you</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0, border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '13px 14px', background: '#FCFBFD' }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--color-gray-600)', marginBottom: 4 }}>Invoices and payment</div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>Your operator</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2, lineHeight: 1.45 }}>
            They set the amount and schedule and handle payment. Parkify reports your parking usage to them.
          </div>
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 0, border: '1px solid var(--color-gray-200)', borderRadius: 10, padding: '13px 14px', background: '#FCFBFD' }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--color-gray-600)', marginBottom: 4 }}>Payment details</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--color-gray-700)' }}>Held by your operator</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2, lineHeight: 1.45 }}>
            Parkify does not have access to those details. Update them with your operator.
          </div>
        </div>
      </div>
    </div>
  )
}

export function Billing({ session, onSubtitleChange }: {
  session: TenantSession
  /** Updates the shared page header from the server's invoicing axis. */
  onSubtitleChange?: (subtitle: string | null) => void
}) {
  const [invoicedBy, setInvoicedBy] = useState<'parkify_bills' | 'operator_bills'>('parkify_bills')
  const [current, setCurrent] = useState<TenantAccrual | null>(null)
  const [invoices, setInvoices] = useState<TenantInvoiceRecord[]>([])
  const [openInvoice, setOpenInvoice] = useState<TenantInvoiceRecord | null>(null)
  // Three states, not two: an estimate, no estimate (a settled answer), or an
  // estimate we couldn't read. The wire keeps the legacy `permitStatement`
  // field for compatibility; this surface must not inherit that old label.
  const [estimate, setEstimate] = useState<PermitStatement | null>(null)
  const [estimateUnavailable, setEstimateUnavailable] = useState(false)
  const [parkingIncluded, setParkingIncluded] = useState(false)
  const [permitBilling, setPermitBilling] = useState<TenantPermitBilling | null>(null)
  const [permitCount, setPermitCount] = useState<number | null>(null)
  // Distinct from "the period was empty". A failed read must never render as
  // $0.00 — see the api.ts note.
  const [err, setErr] = useState('')

  const load = async () => {
    setErr('')
    setOpenInvoice(null)
    try {
      const res = await getTenantBilling(session.token)
      setInvoicedBy(res.invoicedBy)
      setCurrent(res.current)
      setInvoices(res.invoices)
      setEstimate(res.permitStatement ?? null)
      setEstimateUnavailable(!!res.permitStatementUnavailable)
      // Austin distinguishes operator-billed usage from an included lease at
      // the page header. The live response gives us the invoicing axis here,
      // and the permit endpoint gives us the explicit included model. Keep the
      // free language scoped to staff parking: live does not record who funds
      // validation discounts, so "everything is free" would overclaim.
      try {
        const permits = await listRegistrations(session.token)
        const included = permits.billing?.model === 'included'
        setParkingIncluded(included)
        setPermitBilling(permits.billing ?? null)
        setPermitCount(permits.seats.used)
        onSubtitleChange?.(
          res.invoicedBy === 'operator_bills'
            ? 'Your parking usage. Your operator invoices you.'
            : included
              ? 'Staff parking is included in your lease.'
              : null,
        )
      } catch {
        setParkingIncluded(false)
        setPermitBilling(null)
        setPermitCount(null)
        onSubtitleChange?.(res.invoicedBy === 'operator_bills'
          ? 'Your parking usage. Your operator invoices you.' : null)
      }
    } catch (e) {
      setCurrent(null)
      setEstimate(null); setEstimateUnavailable(false)
      setParkingIncluded(false)
      setPermitBilling(null); setPermitCount(null)
      onSubtitleChange?.(null)
      setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] billing', e)
    }
  }

  useEffect(() => {
    if (session.kind === 'device') return   // a tablet 403s here; App.tsx never routes one
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (session.kind === 'device') return null

  return (
    <div>
      {/* The framing sits ABOVE the number, not in a footnote under it. A
          manager who reads the figure first and the caveat second has already
          formed the wrong impression. */}
      <div style={{ background: 'rgba(127,63,152,0.06)', border: '1px solid rgba(127,63,152,0.18)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 4 }}>
          {invoicedBy === 'operator_bills' ? 'Usage your operator can reconcile' : 'What your validations are worth'}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.55 }}>
          {invoicedBy === 'operator_bills'
            ? <>These are the parking discounts your validations took off visitors’ bills. Parkify reports the usage only; <b>your operator handles any invoice or payment</b> under your agreement with them.</>
            : parkingIncluded
              ? <><b>Staff parking is included in your lease.</b> These are the parking discounts your validations gave visitors — not a staff-parking charge. Whether any validation value is charged back depends on your agreement with your operator.</>
            : <>These are the parking discounts your validations took off your visitors' bills — <b>not an amount you owe</b>. Whether any of it is charged back to you depends on your agreement with your parking operator; if you're not sure, ask them.</>}
        </div>
      </div>

      {err && (
        <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 16 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load your billing</div>
            {/* The server's message only ever restates the heading (this endpoint
                has one failure mode: the read didn't answer). What a manager
                needs is the thing the error doesn't say — that no figure is
                being shown, rather than a figure of zero. */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
              This is a loading problem, not a total of zero — we couldn't read your validation history just now.
            </div>
          </div>
          <button onClick={() => void load()} style={purpleBtn}>Retry</button>
        </div>
      )}

      {!err && current && (
        <>
          <BillingSummary current={current} invoicedBy={invoicedBy} permitBilling={permitBilling} permitCount={permitCount} />
          <AccountState invoicedBy={invoicedBy} invoices={invoices} />
          <CurrentPeriod accrual={current} />

          {invoicedBy === 'operator_bills' && <OperatorBillingCard />}

          {/* Nothing at all when the operator has entered no rates — an absent
              estimate must never render as a zeroed one. */}
          {estimate && <StaffParkingEstimate estimate={estimate} />}
          {!estimate && estimateUnavailable && <EstimateUnavailable onRetry={() => void load()} />}

          {invoicedBy === 'parkify_bills' && <div style={{ marginTop: 26 }}>
            <div style={{ ...labelStyle, marginBottom: 8 }}>Invoices</div>
            {invoices.length === 0 ? (
              <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: 28, textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)' }}>
                No earlier statements yet. Once a period closes, your operator prepares a statement and it appears here.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {invoices.map(i => <InvoiceRow key={i.id} invoice={i} onView={() => setOpenInvoice(i)} />)}
              </div>
            )}
          </div>}
        </>
      )}
      {openInvoice && <InvoiceDetail invoice={openInvoice} onClose={() => setOpenInvoice(null)} />}
    </div>
  )
}

/** The accruing period — the headline number, framed. */
function CurrentPeriod({ accrual: a }: { accrual: TenantAccrual }) {
  const validations = a.lines.find(l => l.kind === 'validation_discounts')

  return (
    <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '18px 20px', marginTop: 20 }}>
      <div style={{ ...labelStyle, marginBottom: 6 }}>This period so far</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.5px' }}>{money(a.totalCents)}</div>
        {/* The label is attached to the number itself, so it survives being
            screenshotted or skimmed away from the note above. */}
        <div style={{ fontSize: 13, color: 'var(--color-gray-600)', fontWeight: 600 }}>
          in discounts given
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 6 }}>
        {periodLabel(a)} · counted in {tzLabel(a.timezone, a.periodStartAt)}
        {validations ? ` · ${validations.label}` : ''}
      </div>

      <Omissions accrual={a} />
    </div>
  )
}

/**
 * The current staff-parking estimate — the one place in this product that shows
 * a tenant a report-only projection from the live roster.
 *
 * ⚠️ EVERY NUMBER HERE IS THE OPERATOR'S. Parkify stores the rate they typed and
 * multiplies it by spaces held; it derives nothing. So the copy names the
 * operator as the source twice — once above the figure and once attached to it —
 * and never presents the estimate as an invoice. There is no due date, no
 * balance, and no way to pay from this screen, because it creates no debt and
 * changes no invoice.
 *
 * The multiplication is shown per line ("12 × $75.00 = $900.00") rather than
 * only a total, deliberately: a wrong rate is then visible as a wrong LINE to
 * the manager who knows the real number, which is the only real check on a
 * figure Parkify cannot verify.
 */
export function StaffParkingEstimate({ estimate: s }: { estimate: PermitStatement }) {
  return (
    <div style={{ marginTop: 26 }}>
      <div style={{ ...labelStyle, marginBottom: 8 }}>Staff parking estimate</div>

      {/* Framing ABOVE the figure — a manager who reads the number first and the
          caveat second has already formed the wrong impression. */}
      <div style={{ background: 'rgba(127,63,152,0.06)', border: '1px solid rgba(127,63,152,0.18)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 4 }}>
          Current estimate from your staff spaces
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.55 }}>
          This is a <b>report-only estimate</b>: current spaces multiplied by monthly rates your operator
          recorded. It is <b>not a statement, invoice, bill, or request for payment</b>. Parkify does not add
          it to or change an invoice. If a rate looks wrong, your operator is the one who can change it.
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '18px 20px', marginTop: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {s.lines.map(l => <StatementLine key={`${l.permitType}|${l.zoneIds.join(',')}`} line={l} />)}
        </div>

        <div style={{
          borderTop: '1.5px solid var(--color-gray-200)', marginTop: 14, paddingTop: 12,
          display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px' }}>{money(s.subtotalCents)}</div>
          {/* The qualifier is attached to the number so it survives being
              screenshotted away from the note above. Under a lease allowance the
              total is explicitly a BEFORE figure — the allowance is not netted,
              because which spaces it covers is the operator's call. */}
          <div style={{ fontSize: 13, color: 'var(--color-gray-600)', fontWeight: 600 }}>
            {s.beforeLeaseAllowance
              ? 'estimated per month, before your lease allowance'
              : 'estimated per month, at your operator\u2019s rates'}
          </div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 6 }}>
          {`${fmtDate(s.periodStart)} \u2013 ${fmtDate(s.periodEnd)}`} · counted {fmtDateTime(s.countedAt, s.timezone)}
        </div>
        {/* Said out loud because it is the honest limit of the figure: spaces are
            counted at THIS instant, not averaged over the month. api/src/tenants/
            invoices.ts refuses to put a seat count on an invoice for exactly this
            reason, and this screen keeps that refusal by being explicit instead. */}
        <div style={{ fontSize: 12, color: 'var(--color-gray-600)', lineHeight: 1.5, marginTop: 4 }}>
          This estimate uses the spaces you hold right now — not a month-to-date average or stored history.
          Adding or removing a space changes this report the next time you view it; it does not change an
          invoice or create a charge.
        </div>

        <EstimateOmissions estimate={s} />
      </div>
    </div>
  )
}

/** One line, with the arithmetic spelled out rather than only its result. */
function StatementLine({ line: l }: { line: PermitStatementLine }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 220px', minWidth: 180 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {l.count} {l.typeLabel.toLowerCase()} {l.count === 1 ? 'space' : 'spaces'}
        </div>
        {/* Lot names, because a per-lot rate is meaningless without them — and
            a space valid in several lots is ONE space, priced once. */}
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>
          {l.zoneNames.join(' + ')}
          {l.zoneNames.length > 1 ? ' (one space, valid in both)' : ''}
        </div>
      </div>
      {/* ⚠️ FIXED WIDTH, right-aligned — this column is the whole point of the
          per-line arithmetic. The module note says the multiplication is shown
          per line "rather than only a total" so that a wrong rate is visible as
          a wrong LINE to the manager who knows the real number. That check is a
          visual scan down a column, and the column was ragged: "9 × $75.00/mo"
          and "2 × $180.00/mo" are different widths, and the flexible name cell
          absorbed the difference, so the two started 7px apart. Numbers a reader
          is meant to compare have to line up on their digits.

          Sub-pixel honesty: this lands the digits within ~0.8px, not exactly 0 —
          the flexible name cell beside it still resolves to a fractional width,
          since its content differs per line. Pinning it to exactly 0 means making
          the row a grid, which would then need explicit responsive handling to
          keep the `flexWrap` stacking this row relies on at narrow widths. Not
          worth a regression on mobile for a difference nobody can see. */}
      <div style={{
        fontSize: 13, color: 'var(--color-gray-600)', fontWeight: 600,
        whiteSpace: 'nowrap', flex: `0 0 ${RATE_COL}px`, textAlign: 'right',
      }}>
        {l.count} × {money(l.rateCents)}/mo
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
        {money(l.subtotalCents)}
      </div>
    </div>
  )
}

/**
 * What the estimate CANNOT price, rendered verbatim from the server with a
 * space count and no amount.
 *
 * Shown for the same reason the validation total's omissions are: a manager who
 * is never told which of their spaces are missing from a figure will reasonably
 * conclude those spaces are free.
 */
function EstimateOmissions({ estimate: s }: { estimate: PermitStatement }) {
  if (s.omissions.length === 0) return null
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--color-gray-100)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {s.omissions.map(o => (
        <div key={o.kind} style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
          {o.reason}
        </div>
      ))}
    </div>
  )
}

/**
 * The third state: rates ARE configured, but we couldn't read the permits to
 * count them.
 *
 * Rendered instead of nothing, because "your operator hasn't set rates" and "we
 * couldn't load them" are different facts and a tenant who sees the settled-
 * looking silence would conclude their rates were removed. Carries no figure.
 */
function EstimateUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <div style={{ marginTop: 26 }}>
      <div style={{ ...labelStyle, marginBottom: 8 }}>Staff parking estimate</div>
      <div style={{
        background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Couldn't load your current estimate</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
            Your operator has recorded rates, but we couldn't count your spaces just now — so no figure is
            being shown rather than a wrong one.
          </div>
        </div>
        <button onClick={onRetry} style={purpleBtn}>Retry</button>
      </div>
    </div>
  )
}

function InvoiceRow({ invoice: i, onView }: { invoice: TenantInvoiceRecord; onView: () => void }) {
  const tone = STATUS_TONE[i.status]
  const validations = i.lines.find(l => l.kind === 'validation_discounts')

  return (
    <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 200, flex: '1 1 240px' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{periodLabel(i)}</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>
          {validations ? validations.label : 'No validations'} · counted in {tzLabel(i.timezone, i.periodStartAt)}
        </div>
      </div>
      {/* Same fix, and the same reason, as the roster's Status column (#1841):
          the pill sized to its own LABEL, and "Issued" (68px) / "Paid" (53px) /
          "Void" (54px) are different widths, so the pills sat at three different
          x positions down the list. The amounts never moved — they are pinned by
          `textAlign: right` — but a ragged pill column still reads as sloppy on
          the one screen a manager scans for what they owe. Fixed cell, pill sized
          to its text inside it. */}
      <div style={{ flex: `0 0 ${STATUS_COL}px`, display: 'flex' }}>
        <span style={{
          fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px',
          padding: '4px 10px', borderRadius: 999,
          background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
        }}>{tone.label}</span>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{money(i.totalCents)}</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)' }}>in discounts given</div>
      </div>
      <button type="button" onClick={onView} style={{ ...purpleBtn, fontSize: 12, padding: '8px 12px' }} aria-label={`View details for ${i.id}`}>
        View details
      </button>
    </div>
  )
}

/**
 * A stored statement's actual lines, without implying it is a payable bill.
 * The demo opens invoice details, but its Pay and Download controls are
 * explicitly unavailable. Live has no due date, payable balance, or collection
 * action here, so this panel only exposes the recorded discount breakdown.
 */
function InvoiceDetail({ invoice: i, onClose }: { invoice: TenantInvoiceRecord; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => { onCloseRef.current = onClose }, [onClose])
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus()
    }
  }, [])

  return (
    <div onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(16, 12, 23, 0.62)', display: 'grid', placeItems: 'center', padding: 18 }}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="invoice-detail-title"
        style={{ width: 'min(520px, 100%)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 24px 70px rgba(0,0,0,.22)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={labelStyle}>Statement details</div>
            <h2 id="invoice-detail-title" style={{ fontSize: 20, margin: '5px 0 4px' }}>{periodLabel(i)}</h2>
            <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>{i.id} · {STATUS_TONE[i.status].label} · counted in {tzLabel(i.timezone, i.periodStartAt)}</div>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close statement details"
            style={{ background: 'none', border: 0, color: 'var(--color-gray-600)', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--color-gray-700)', margin: '18px 0' }}>
          This records the value of parking discounts given. It is not an amount due or a request for payment.
        </p>
        <div style={{ borderTop: '1px solid var(--color-gray-200)' }}>
          {i.lines.map(line => (
            <div key={line.kind} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--color-gray-200)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{line.label}</div>
                <div style={{ fontSize: 11, color: 'var(--color-gray-600)' }}>{line.quantity} {line.quantity === 1 ? 'validation' : 'validations'} counted</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap' }}>{money(line.amountCents)}</div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '14px 0', fontSize: 14, fontWeight: 800 }}>
            <span>Total value of discounts</span><span>{money(i.totalCents)}</span>
          </div>
        </div>
        <Omissions accrual={i} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 22 }}>
          <button type="button" onClick={onClose} style={purpleBtn}>Close</button>
        </div>
      </section>
    </div>
  )
}

/**
 * What the total does NOT cover. Rendered verbatim from the server, and shown on
 * every period — a manager who is never told that staff parking is excluded will
 * reasonably conclude it costs nothing.
 */
function Omissions({ accrual: a }: { accrual: TenantAccrual }) {
  if (a.omissions.length === 0) return null
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--color-gray-100)', paddingTop: 12 }}>
      {a.omissions.map(o => (
        <div key={o.kind} style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
          {o.reason}
        </div>
      ))}
    </div>
  )
}
